import { useSessionStorage } from '@vueuse/core';
import { NOTIFICATION_TYPE } from '~/composables/useNotifications';
import { AuthVia, AuthViaStorageKey } from '~/enums/auth.enum';

export default defineNuxtRouteMiddleware(async (to, from) => {
  const { checkAuthenticationStatus, authenticatedVia } = useAuthentication();
  const { getUnreadNotifications, markAsRead } = useNotifications();
  const { $i18n } = useNuxtApp();

  // Extract path without locale prefix for comparison
  const pathWithoutLocale = to.path.replace(/^\/(en|de|fr|es)/, '');
  // Exclude routes that do not require authentication
  if (
    to.path.includes('/invite/accept') ||
    pathWithoutLocale === '/auth/login' ||
    pathWithoutLocale === '/auth/signup' ||
    // TODO(@hayk): check if this page exists. if not — remove it.
    pathWithoutLocale === '/auth/signup-prompt' ||
    pathWithoutLocale === '/auth/verify-email' ||
    pathWithoutLocale === '/user/login' ||
    pathWithoutLocale === '/user/register' ||
    pathWithoutLocale === '/user/password'
  ) {
    return;
  }

  const isAuthenticated = await checkAuthenticationStatus();

  const isResetPassword = pathWithoutLocale === '/auth/reset-password';
  const isForgotPassword = pathWithoutLocale === '/auth/forgot-password';

  if (isResetPassword || isForgotPassword) {
    if (isAuthenticated) {
      if (isResetPassword) {
        return navigateTo('/dashboard');
      }

      if (isForgotPassword) {
        return navigateTo('/account/profile-info');
      }
    }

    return;
  }

  if (!isAuthenticated) {
    // this is required to prevent 404 error when not authenticated.
    await clearError();

    const qs = new URLSearchParams();

    const isRootPath = to.path === '/' || to.path.match(/^\/(en|de|fr|es)\/?$/);
    if (!isRootPath) {
      qs.set('next',to.fullPath);
    }

    // google cross domain tracking parameter
    if (to.query._gl) {
      qs.set('_gl', to.query._gl as string);
    }

    const locale = $i18n?.locale?.value || 'en';
    const loginUrl = locale === 'en' ? '/auth/login' : `/${locale}/auth/login`;

    return navigateTo(`${loginUrl}?${qs.toString()}`, {
      replace: true,
    });
  }

  const { user } = useUserStore();

  if (!user?.value?.isVerified) {
    const locale = $i18n?.locale?.value || 'en';
    const verifyUrl = locale === 'en' ? '/auth/verify-email' : `/${locale}/auth/verify-email`;
    return navigateTo(verifyUrl, {
      replace: true,
    });
  }

  if (!user?.value?.registrationCompleted && !to.path.includes('/auth/about-you')) {
    const locale = $i18n?.locale?.value || 'en';
    const aboutYouUrl = locale === 'en' ? '/auth/about-you' : `/${locale}/auth/about-you`;
    return navigateTo(aboutYouUrl, {
      replace: true,
    });
  }

  if (user?.value?.numberOfUnreadNotifications && (to.path === '/dashboard' || to.path === '/error/thank-you')) {
    const notifications = await getUnreadNotifications();
    if (notifications?.length) {
      const groupInvitationAcceptedUnreadNotification = notifications.find(notification =>
        notification.data.notificationName === NOTIFICATION_TYPE.B2B_INVITATION_ACCEPTED &&
        !notification.readAt,
      );

      if (groupInvitationAcceptedUnreadNotification) {
        await markAsRead(groupInvitationAcceptedUnreadNotification.id);
        if (user?.value) {
          user.value.numberOfUnreadNotifications--;
        }

        // new user case (set password -> login -> dashboard -> thank-you page)
        if (to.path === '/dashboard' && from.path !== '/enroll/thank-you') {
          return navigateTo('/enroll/thank-you', {
            replace: true,
          });
        }
      }
    }
  }

  // mobile app login
  if (pathWithoutLocale !== '/auth/about-you') {
    const authVia = useSessionStorage(AuthViaStorageKey.AuthVia, null, { listenToStorageChanges: false }).value;

    if (authVia === AuthVia.MedmasteryApp) {
      await authenticatedVia(AuthVia.MedmasteryApp);
    }
  }
});